import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, CircularProgress,
  Avatar, Chip, IconButton, Paper, Stack, ToggleButton, ToggleButtonGroup,
  Card, CardContent
} from '@mui/material';
import { Add, Search, Person, Delete, CloudUpload, ViewList, ViewModule } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { customersAPI } from '../services/api';
import DataTable, { StatusChip } from '../components/common/DataTable';
import { toast } from 'react-toastify';
import {
  getCustomerDocumentUrl,
  isCustomerPhotoDocument,
} from '../utils/customerDocuments';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const CustomerForm = ({ open, onClose, customer, onSaved }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [customerPhoto, setCustomerPhoto] = useState(null);
  const [customerPhotoPreview, setCustomerPhotoPreview] = useState(null);
  const [existingCustomerPhotoPreview, setExistingCustomerPhotoPreview] = useState(null);
  const [idDocuments, setIdDocuments] = useState([]);
  const [idDocumentPreviews, setIdDocumentPreviews] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);

  useEffect(() => {
    reset(customer || { customer_type: 'individual' });
    setCustomerPhoto(null);
    setCustomerPhotoPreview(null);
    setExistingCustomerPhotoPreview(null);
    setIdDocuments([]);
    setIdDocumentPreviews([]);
    if (customer?.id) {
      // Load existing documents when editing
      loadCustomerDocuments(customer.id);
    } else {
      setExistingDocuments([]);
    }
  }, [customer, open, reset]);

  const loadCustomerDocuments = async (customerId) => {
    try {
      const { data } = await customersAPI.getDocuments(customerId);
      const docs = data.data || [];
      const existingPhoto = docs.find(isCustomerPhotoDocument);
      const photoPreview = existingPhoto ? getCustomerDocumentUrl(existingPhoto) : null;

      setExistingDocuments(docs.filter((doc) => !isCustomerPhotoDocument(doc)));
      setExistingCustomerPhotoPreview(photoPreview);
      setCustomerPhotoPreview(photoPreview);
    } catch {
    }
  };

  const handleCustomerPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setCustomerPhoto(file);
      setCustomerPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleIdDocumentChange = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = [];
    const newDocuments = [];

    files.forEach((file) => {
      if (file.type.startsWith('image/') || file.type.includes('pdf')) {
        const preview = URL.createObjectURL(file);
        newPreviews.push(preview);
        newDocuments.push(file);
      }
    });

    setIdDocumentPreviews([...idDocumentPreviews, ...newPreviews]);
    setIdDocuments([...idDocuments, ...newDocuments]);
  };

  const removeIdDocument = (index) => {
    const newDocuments = idDocuments.filter((_, i) => i !== index);
    const newPreviews = idDocumentPreviews.filter((_, i) => i !== index);
    setIdDocuments(newDocuments);
    setIdDocumentPreviews(newPreviews);
  };

  const removeCustomerPhoto = () => {
    setCustomerPhoto(null);
    setCustomerPhotoPreview(existingCustomerPhotoPreview);
  };

  const deleteExistingDocument = async (documentId) => {
    try {
      await customersAPI.deleteDocument(customer.id, documentId);
      setExistingDocuments(existingDocuments.filter(doc => doc.id !== documentId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // First create/update customer
      let customerData;
      if (customer?.id) {
        const response = await customersAPI.update(customer.id, data);
        customerData = response.data.data;
        toast.success('Customer updated');
      } else {
        const response = await customersAPI.create(data);
        customerData = response.data.data;
        toast.success('Customer created');
      }

      // Upload customer photo if provided
      if (customerPhoto) {
        const photoFormData = new FormData();
        photoFormData.append('photo', customerPhoto);
        await customersAPI.uploadDocuments(customerData.id, photoFormData);
      }

      // Upload ID documents if provided
      if (idDocuments.length > 0) {
        const docFormData = new FormData();
        idDocuments.forEach((doc) => {
          docFormData.append('id_card', doc);
        });
        await customersAPI.uploadDocuments(customerData.id, docFormData);
      }

      onSaved(customerData);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>{customer?.id ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        <DialogContent dividers sx={{ pb: 3 }}>
          {/* Customer Photo Section */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Customer Photo</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={customerPhotoPreview}
                sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
              >
                <Person sx={{ fontSize: 40 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <input
                  accept="image/*"
                  id="customer-photo-upload"
                  type="file"
                  hidden
                  onChange={handleCustomerPhotoChange}
                />
                <label htmlFor="customer-photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    size="small"
                  >
                    Upload Photo
                  </Button>
                </label>
                {customerPhoto && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={removeCustomerPhoto}
                    sx={{ ml: 1 }}
                  >
                    <Delete />
                  </IconButton>
                )}
                <Typography variant="caption" display="block" color="text.secondary">
                  Upload a clear photo of the customer (JPG, PNG)
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* ID Documents Section */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>ID Documents</Typography>
            
            {/* Existing Documents */}
            {existingDocuments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Existing Documents:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {existingDocuments.map((doc) => (
                    <Chip
                      key={doc.id}
                      label={`${doc.document_type.replace('_', ' ')} (${doc.file_name})`}
                      onDelete={() => deleteExistingDocument(doc.id)}
                      deleteIcon={<Delete />}
                      size="small"
                      color="primary"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* New Documents */}
            <Box>
              <input
                accept="image/*,.pdf"
                id="id-documents-upload"
                type="file"
                hidden
                multiple
                onChange={handleIdDocumentChange}
              />
              <label htmlFor="id-documents-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  size="small"
                >
                  Add ID Documents
                </Button>
              </label>
              
              {idDocumentPreviews.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    New Documents:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {idDocumentPreviews.map((preview, index) => (
                      <Chip
                        key={index}
                        label={`Document ${index + 1}`}
                        onDelete={() => removeIdDocument(index)}
                        deleteIcon={<Delete />}
                        size="small"
                        color="secondary"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              
              <Typography variant="caption" display="block" color="text.secondary">
                Upload ID cards, driver's license, passport, etc. (Images or PDF)
              </Typography>
            </Box>
          </Paper>

          {/* Customer Information */}
          <Typography variant="h6" fontWeight={600} gutterBottom>Customer Information</Typography>
          <Grid container spacing={2}>
            {[
              { name: 'first_name', label: 'First Name', required: true, xs: 12, sm: 6 },
              { name: 'last_name', label: 'Last Name', required: true, xs: 12, sm: 6 },
              { name: 'email', label: 'Email', xs: 12, sm: 6 },
              { name: 'phone', label: 'Phone', xs: 12, sm: 6 },
              { name: 'national_id', label: 'National ID / License', xs: 12, sm: 6 },
              { name: 'date_of_birth', label: 'Date of Birth', type: 'date', xs: 12, sm: 6 },
              { name: 'address', label: 'Address', xs: 12 },
              { name: 'city', label: 'City', xs: 12, sm: 4 },
              { name: 'state', label: 'State', xs: 12, sm: 4 },
              { name: 'country', label: 'Country', xs: 12, sm: 4 },
              { name: 'company_name', label: 'Company (if corporate)', xs: 12, sm: 6 },
              { name: 'source', label: 'Lead Source', xs: 12, sm: 6 },
            ].map(({ name, label, required, type, xs, sm }) => (
              <Grid item xs={xs} sm={sm} key={name}>
                <TextField fullWidth label={label} type={type || 'text'}
                  InputLabelProps={type === 'date' ? { shrink: true } : undefined}
                  {...register(name, required ? { required: `${label} is required` } : {})}
                  error={!!errors[name]} helperText={errors[name]?.message} />
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} {...register('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (customer?.id ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const CustomersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [customerPhotos, setCustomerPhotos] = useState({}); // Store customer photos by ID
  const [redirectContext, setRedirectContext] = useState(null);

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setViewMode(nextView);
    }
  };

  const fetchCustomerPhotos = async (customerList) => {
    const photoPromises = customerList.map(async (customer) => {
      try {
        const { data } = await customersAPI.getDocuments(customer.id);
        const docs = data.data || [];
        const customerPhoto = docs.find(isCustomerPhotoDocument);
        return {
          customerId: customer.id,
          photoUrl: customerPhoto ? getCustomerDocumentUrl(customerPhoto) : null
        };
      } catch {
        return { customerId: customer.id, photoUrl: null };
      }
    });

    const photoResults = await Promise.all(photoPromises);
    const photoMap = {};
    photoResults.forEach(({ customerId, photoUrl }) => {
      if (photoUrl) {
        photoMap[customerId] = photoUrl;
      }
    });
    setCustomerPhotos(photoMap);
  };

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await customersAPI.getAll({ page: page + 1, limit: rowsPerPage, search: search || undefined });
      const customerList = data.data || [];
      setCustomers(customerList);
      setTotal(data.pagination?.total || 0);
      
      // Fetch customer photos for card view
      if (viewMode === 'cards' && customerList.length > 0) {
        await fetchCustomerPhotos(customerList);
      }
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, viewMode]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    if (!location.state?.openCreateCustomer) return;

    setRedirectContext({
      returnTo: location.state.returnTo || null,
      returnSystem: location.state.returnSystem || 'car_sales',
      reopenOrder: location.state.reopenOrder !== false,
    });
    setEditCustomer(null);
    setFormOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const handleCustomerSaved = async (savedCustomer) => {
    await fetchCustomers();

    if (redirectContext?.returnTo && savedCustomer?.id) {
      localStorage.setItem('activeUnit', redirectContext.returnSystem);
      navigate(redirectContext.returnTo, {
        replace: true,
        state: {
          openCreateOrder: redirectContext.reopenOrder,
          createdCustomer: savedCustomer,
          returnSystem: redirectContext.returnSystem,
        },
      });
      setRedirectContext(null);
    }
  };

  const columns = [
    { key: 'customer_code', label: 'Code', render: (v) => <Typography variant="body2" fontFamily="monospace" fontWeight={600}>{v}</Typography> },
    { key: 'first_name', label: 'Name', render: (_, row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
          {row.first_name?.[0]}{row.last_name?.[0]}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={600}>{row.first_name} {row.last_name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </Box>
      </Box>
    )},
    { key: 'phone', label: 'Phone' },
    { key: 'customer_type', label: 'Type', render: (v) => (
      <Chip label={v} size="small" color={v === 'corporate' ? 'primary' : 'default'} variant="outlined" />
    )},
    { key: 'total_orders', label: 'Orders', align: 'right' },
    { key: 'total_spent', label: 'Total Spent', align: 'right', render: (v) => <Typography fontWeight={600} color="success.main">{fmt(v)}</Typography> },
    { key: 'is_active', label: 'Status', render: (v) => <StatusChip status={v ? 'active' : 'inactive'} /> },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button size="small" variant="outlined" onClick={() => navigate(`/customers/${v}`)}>View</Button>
        <Button size="small" onClick={() => { setEditCustomer(row); setFormOpen(true); }}>Edit</Button>
      </Box>
    )},
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Customers</Typography>
          <Typography variant="body2" color="text.secondary">{total} total customers</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            size="small"
            sx={{ mr: 2 }}
          >
            <ToggleButton value="table" aria-label="table view">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="cards" aria-label="card view">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditCustomer(null); setFormOpen(true); }}>
            Add Customer
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField placeholder="Search customers..." size="small" sx={{ minWidth: 300 }}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Box>

      {/* Conditional rendering based on view mode */}
      {viewMode === 'table' ? (
        <DataTable columns={columns} rows={customers} loading={loading} total={total}
          page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} />
      ) : (
        <Grid container spacing={2}>
          {customers.map((customer) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={customer.id}>
              <Card sx={{ 
                height: 260, 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': { 
                  transform: 'translateY(-2px)', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)' 
                }
              }}>
                {/* Photo Section - Takes appropriate space */}
                <Box sx={{ 
                  position: 'relative', 
                  height: 160,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50'
                }}>
                  {customerPhotos[customer.id] ? (
                    <Avatar 
                      src={customerPhotos[customer.id]}
                      alt={`${customer.first_name} ${customer.last_name}`}
                      sx={{ 
                        width: 120, 
                        height: 120,
                        border: '3px solid white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                  ) : (
                    <Avatar sx={{ 
                      width: 120, 
                      height: 120, 
                      bgcolor: 'primary.main', 
                      fontSize: '3rem',
                      border: '3px solid white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      {customer.first_name?.[0]}{customer.last_name?.[0]}
                    </Avatar>
                  )}
                  
                  {/* Status Badge */}
                  <StatusChip 
                    status={customer.is_active ? 'active' : 'inactive'} 
                    sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8,
                      fontSize: '0.6rem',
                      height: 20,
                      bgcolor: 'white',
                      border: '1px solid',
                      borderColor: customer.is_active ? 'success.main' : 'error.main'
                    }} 
                  />
                </Box>
                
                {/* Details Section - Fixed height for buttons */}
                <CardContent sx={{ py: 2, px: 1.5, textAlign: 'center', bgcolor: 'grey.50', flexGrow: 0 }}>
                  <Typography variant="h6" fontWeight={600} noWrap sx={{ mb: 0.5, fontSize: '0.85rem', lineHeight: 1.2 }}>
                    {customer.first_name} {customer.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem', mb: 1.5 }}>
                    {customer.customer_code}
                  </Typography>
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      sx={{ fontSize: '0.7rem', py: 0.5, px: 1.25, minWidth: 'auto' }}
                    >
                      View
                    </Button>
                    <Button 
                      size="small" 
                      variant="contained"
                      onClick={() => { setEditCustomer(customer); setFormOpen(true); }}
                      sx={{ fontSize: '0.7rem', py: 0.5, px: 1.25, minWidth: 'auto' }}
                    >
                      Edit
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CustomerForm open={formOpen} onClose={() => setFormOpen(false)}
        customer={editCustomer} onSaved={handleCustomerSaved} />
    </Box>
  );
};

export default CustomersPage;
